import React from 'react';
import SEO from '../components/SEO';
import usePage from '../hooks/usePage';
import Reveal from '../components/ui/Reveal';

const DEFAULT_CONTENT = `<p>&ldquo;<em><strong>St. Andrews World School has been accredited with the &lsquo;Excellence in Modernization Education in Schooling&rsquo; Award, which marks the excellence of our modern curriculum.&rdquo;</strong></em></p>

<p>We believe in value-based learning and hence have structured our curriculum in a way that is both innovative and effective. St. Andrews World School follows the CBSE curriculum which is fine-tuned to fit the challenges of today and also progress towards the new paradigm of education for the 21st century.</p>

<h3>Subjects Offered</h3>

<ul>
<li>Languages</li>
<li>Arts &amp; Music</li>
<li>Science &amp; Technology</li>
<li>Social Sciences</li>
<li>Information Technology &amp; Coding</li>
<li>Sports</li>
</ul>

<h3>Infrastructure</h3>

<ul>
<li>Outdoor and indoor play area for kids</li>
<li>Modern digital classrooms</li>
<li>Dance room</li>
<li>Music room</li>
<li>Sports arena</li>
<li>Recreation room</li>
<li>Computer lab</li>
<li>Fully provisioned library</li>
</ul>`;

const SUBJECTS = [
  { icon: 'fa-language', title: 'Languages', text: 'English, Hindi and an additional language, building strong communication skills.' },
  { icon: 'fa-palette', title: 'Arts & Music', text: 'Creative expression through fine arts, dance and music.' },
  { icon: 'fa-flask', title: 'Science & Technology', text: 'Hands-on science learning from our well-equipped laboratories.' },
  { icon: 'fa-globe', title: 'Social Sciences', text: 'Understanding society, history and global perspectives.' },
  { icon: 'fa-code', title: 'IT & Coding', text: 'Digital literacy and coding to prepare students for a tech-driven future.' },
  { icon: 'fa-futbol', title: 'Sports', text: 'Physical development, teamwork and sportsmanship through daily play.' },
];

const FACILITIES = [
  { icon: 'fa-shapes', title: 'Play Area', text: 'Safe outdoor and indoor play areas designed for young learners.' },
  { icon: 'fa-chalkboard-user', title: 'Digital Classrooms', text: 'Modern classrooms equipped with smart learning technology.' },
  { icon: 'fa-person-walking-dotted-line', title: 'Music Room', text: 'A dedicated space to explore rhythm and vocal skills.' },
  { icon: 'fa-person-dots-from-line', title: 'Dance Room', text: 'A spacious studio for movement, dance and expression.' },
  { icon: 'fa-medal', title: 'Sports Arena', text: 'A sprawling arena for games, athletics and physical activities.' },
  { icon: 'fa-people-group', title: 'Recreation Room', text: 'A fun and relaxing space for recreational activities and play.' },
  { icon: 'fa-microchip', title: 'Computer Lab', text: 'A modern lab for hands-on digital and coding education.' },
  { icon: 'fa-book-open-reader', title: 'Library', text: 'A fully provisioned library encouraging a love for reading.' },
];

const CURRICULUM_DESC = `We believe in value-based learning and have structured our curriculum in a way that is both innovative and effective. St. Andrews World School follows the CBSE curriculum, fine-tuned to fit the challenges of today and progress towards the latest of education for the 21st century.`;

const CurriculumInfrastructure = () => {
  const title = 'Curriculum and Infrastructure';
  const pageData = usePage('curriculum-and-infrastructure', { content: DEFAULT_CONTENT });

  const isCustom = pageData.content && pageData.content.trim() !== DEFAULT_CONTENT.trim();

  return (
    <>
      <SEO
        title={title}
        description="Curriculum details of St. Andrews World School, Siddharth Vihar."
        keywords="Curriculum, Infrastructure, SAWS Siddharth Vihar, CBSE curriculum"
      />

      <section className="page-hero">
        {!pageData.loading && (
        <img
          src={pageData.bannerImage || `${process.env.PUBLIC_URL}/images/page-banners/curricullam-infrastructure.jpg`}
          alt={title}
          className="page-hero-img"
          loading="eager"
        />
        )}
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner" />
          </div>
        </div>
      </section>

      {isCustom ? (
        <section className="section bg-white">
          <div className="container">
            <div className="card p-7 md:p-10 max-w-[1000px] mx-auto">
              <div className="text-body leading-relaxed" dangerouslySetInnerHTML={{ __html: pageData.content }} />
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Intro */}
          <section className="section bg-white">
            <div className="container">
              <div className="max-w-[900px] mx-auto text-center">
                <Reveal>
                  <div className="inline-flex items-center gap-2 bg-primary/5 text-primary px-4 py-2 rounded-full text-sm font-semibold">
                    <i className="fas fa-trophy mr-1 text-secondary"></i> Excellence in Modernization Education
                  </div>
                  <p className="text-xl md:text-2xl leading-relaxed mt-6 text-primary italic">
                    "St. Andrews World School has been accredited with the Excellence in Modernization Education award."
                  </p>
                  <div className="divider-line mx-auto" />
                  <p className="text-body leading-relaxed mt-6 text-[17px]">
                    {CURRICULUM_DESC}
                  </p>
                </Reveal>
              </div>
            </div>
          </section>

          {/* Subjects */}
          <section className="section section-alt">
            <div className="container">
              <div className="section-title">
                <div className="kicker">Our Curriculum</div>
                <h2 className="h-section text-3xl md:text-4xl font-bold">Subjects Offered</h2>
                <div className="divider-line" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {SUBJECTS.map((s, i) => (
                  <Reveal key={s.title} delayMs={i * 60}>
                    <div className="card p-7 h-full group hover:shadow-lg transition-all duration-300">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                        <i className={`fas ${s.icon}`} aria-hidden></i>
                      </div>
                      <h3 className="text-primary font-bold text-[1.2rem] mb-2">{s.title}</h3>
                      <p className="text-body text-[15px] leading-relaxed">{s.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Facilities */}
          <section className="section bg-white">
            <div className="container">
              <div className="section-title">
                <div className="kicker">Our Infrastructure</div>
                <h2 className="h-section text-3xl md:text-4xl font-bold">World-Class Facilities</h2>
                <div className="divider-line" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {FACILITIES.map((f, i) => (
                  <Reveal key={f.title} delayMs={i * 50}>
                    <div className="card p-7 h-full text-center group hover:shadow-lg transition-all duration-300">
                      <div className="w-14 h-14 rounded-full bg-secondary/10 text-secondary flex items-center justify-center mx-auto mb-4 group-hover:bg-secondary group-hover:text-white transition-all duration-300">
                        <i className={`fas ${f.icon} text-xl`} aria-hidden></i>
                      </div>
                      <h3 className="text-primary font-bold text-[1.2rem] mb-2">{f.title}</h3>
                      <p className="text-body text-sm leading-relaxed">{f.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
};

export default CurriculumInfrastructure;